// The acts a device takes in Operate: the bounded commands and the console
// (ZENITH.md §11.2, ADR-0005). One implementation for the two places that
// offer them — the row in the site's table and the head of the device's own
// page (#329) — so a gate, a tooltip or a refusal changes in one place and the
// two cannot disagree about what a device may be sent.
//
// **Two shapes, one list** (#368). In the head the acts are drawn as icons
// with their word beside them, where a reader looks first. On a row they sit
// behind one three-dot button, so a row is a row and not a strip of text
// buttons; the menu is the shared `Popover`, and its items are the same acts
// under the same gates. `actsOf` is the one list both shapes render, so an act
// cannot be offered in one and not the other.
//
// **Only these acts** (#875, ADR-0149 decision 2). Every device act of §11.2
// stays in Operate — blink, reboot, factory reset, the firmware upgrade and
// the console — because they are how an operator troubleshoots and none of
// them authors intent. Renaming, moving between sites, removing a device from
// its site and putting it in the plan are writes of intent and are Design's:
// Design › Physical › Equipment's maintenance menu (`Maintenance.tsx`), one
// click away from a device's page by *Change this in Design*.
//
// **One Actions menu in the head, and the console beside it** (#1889, Gabriel Provencher's review
// of the 2026-10-20 mockup board, concept 2). Until then the head drew Reboot, Blink and Console as
// buttons and a **More ▾** menu held the rest (#1801). Now the console and the page's Refresh are
// the head's only buttons, and **Actions ▾** holds every command in one order: Blink LEDs, Reboot,
// Power cycle, Upgrade firmware… and, last and below a divider, Factory reset…, which keeps its red
// and its confirmation (`ActPanel.tsx`, the typed serial of ADR-0037) unchanged. The row's
// three-dot menu lists the same acts under the same words in the same order, with the console
// before the divider, so the two shapes still read as one list.
//
// The gates are the page's (`can`, `Devices.tsx`); this renders what it is
// told the caller may do and decides nothing about who may act.
import { Fragment, useRef, useState, type ReactNode, type RefObject } from "react";
import { Icon, Popover, type IconName } from "../../ui";
import { absentExplain, ACTIONS, ACTION_ORDER, explainFor, isAbsent, notLiveExplain, type Action, type Device } from "./commands";
import { Glyph, OpenIcon } from "../../icons";

export interface ActionHandlers {
  onAct(d: Device, a: Action): void;
  onConsole(d: Device): void;
  /** The caller holds `device.poe.cycle` at the device's site (ADR-0251). Its
   * own gate and not `mayCommand`'s, because the capability is its own. */
  mayPoeCycle?: boolean;
}

interface Props extends ActionHandlers {
  device: Device;
  /** The page's write lane is busy: every button waits (#189). */
  busy: boolean;
  mayCommand: boolean; mayConsole: boolean;
  /** Where the acts are drawn: icons with words in the page head, or a
   * three-dot menu on a table row. */
  as: "head" | "menu";
  /** Drawn in the head after the console and before Actions: the page's
   * Refresh. Not an act, so the row's menu has no place for it. */
  beside?: ReactNode;
  /** The head's Actions trigger, for a popover its menu opens to anchor at (#2118). */
  trigger?: RefObject<HTMLButtonElement | null>;
  /** The superuser's *CDS record…* (#2118): last in the head's menu, below a divider of its own, and
   * no act — it opens the device's CDS record and sends nothing. Absent, the menu has no such item. */
  onCdsRecord?(): void;
}

/** The order both shapes list the acts in (#1889). Factory reset is last, below a divider. */
const ORDER = ["blink", "reboot", "poe_cycle", "firmware_request", "console", "factory"];

/** One act as both shapes draw it. */
interface Act {
  key: string; label: string; icon: IconName; disabled: boolean; title: string; onClick(): void;
}

/** The acts this device is offered, in the order both shapes list them (#1889). */
function actsOf(props: Props): Act[] {
  return offeredActs(props).sort((x, y) => ORDER.indexOf(x.key) - ORDER.indexOf(y.key));
}

/** The acts this device is offered: the commands, then the console. */
function offeredActs({ as, device: d, busy, mayCommand, mayConsole, mayPoeCycle, onAct, onConsole }: Props): Act[] {
  const acts: Act[] = [];
  const absent = isAbsent(d);
  // A switch's firmware upgrade is refused by the API (§11.2, ADR-0013):
  // arpIQ does not upgrade switch firmware, so the act is not offered
  // rather than offered and refused after a round trip.
  //
  // **One firmware act** (ADR-0404 decision 1c, built by #1816): the firmware
  // request, named Upgrade, in the place the controller-direct upgrade held.
  // That upgrade is deprecated at `/v1` and this page no longer offers it, so
  // the head and the row carry one firmware control and never two. What each
  // candidate costs is the act's panel to say, per candidate, from the server's
  // own reading (`FirmwareAct.tsx`), so no marker on the act stands in
  // for it: the marker this act once carried was about the controller's image,
  // which the act no longer sends.
  //
  // Restates ADR-0403 decision 1a and ADR-0413 decision 8 as amended 2026-10-17 (#1841) — the firmware
  // act is offered on the device page's head alone, where its answer is the Firmware card; a row of
  // the site's table offers no Upgrade (Gabriel Provencher: "remove the upgrade option from this
  // table"), and its marker links to the device's page instead.
  const actions = ACTION_ORDER.filter((a) => a !== "firmware_request" || (d.category === "ap" && as === "head"));
  if (mayCommand) {
    for (const a of actions) {
      // Restates ADR-0367 decision 2.1a — the firmware act refuses a device that is not online, as
      // the commands beside it do, because its click is to approve a delivery that starts now.
      // So it is disabled for an offline access point and for one the controller holds no
      // record of, with the reason the server's refusal gives.
      acts.push({
        key: a, label: ACTIONS[a].menu, icon: a === "firmware_request" ? "upgrade" : a,
        disabled: busy || !d.online || absent,
        title: absent ? absentExplain(d)
          : !d.online ? notLiveExplain(d, a === "firmware_request" ? "a firmware upgrade" : "a command")
          : explainFor(a, d),
        onClick: () => onAct(d, a),
      });
    }
  }
  if (mayPoeCycle && d.category === "ap") {
    // Restates ADR-0252 decision 5a as ruled — the switch must be online and the access
    // point's own state is not a gate, because a hung access point is the act's main case. So
    // the act stays enabled for an offline access point, unlike every command above it; the
    // server answers the refusals, and the panel draws each one.
    acts.push({
      key: "poe_cycle", label: ACTIONS.poe_cycle.menu, icon: "power",
      disabled: busy || absent,
      title: absent ? absentExplain(d) : ACTIONS.poe_cycle.explain,
      onClick: () => onAct(d, "poe_cycle"),
    });
  }
  if (mayConsole) {
    acts.push({
      // No "Hide console": the console is a window of its own now (#537), so
      // this act only ever opens one. Asking for a device whose window is
      // already up focuses that window rather than opening a second.
      key: "console", label: "Console", icon: "console",
      disabled: !d.online || absent,
      title: absent ? absentExplain(d) : d.online ? "Open the device’s own shell in its own window. Nothing typed is recorded; the audit says who opened a console and for how long." : notLiveExplain(d, "a console"),
      onClick: () => onConsole(d),
    });
  }
  return acts;
}

export default function DeviceActions(props: Props) {
  const { as, device: d, beside, trigger, onCdsRecord } = props;
  const acts = actsOf(props);
  if (as === "head") {
    const menu = acts.filter((a) => a.key !== "console");
    const shell = acts.find((a) => a.key === "console");
    return (
      <>
        {shell && <ActButton act={shell} />}
        {beside}
        {(menu.length > 0 || onCdsRecord) && (
          <ActMenu label={`Actions for ${d.name}`} acts={menu} anchorRef={trigger} onCdsRecord={onCdsRecord}
            trigger={<><span>Actions</span><Glyph icon={OpenIcon} size={12} className="seg-caret" /></>} />
        )}
      </>
    );
  }
  // Nothing to offer, nothing to open: a viewer's row has no menu rather
  // than a button that opens an empty one.
  if (acts.length === 0) return null;
  return <ActMenu label={`Actions for ${d.name}`} acts={acts} />;
}

/** One act's button: the icon, then the word, so the accessible name is the
 * word and the tooltip is the explanation — in the head and in the menu alike. */
function ActButton({ act: a, onClick = a.onClick }: { act: Act; onClick?(): void }) {
  return (
    <button type="button" className={"btn btn-quiet btn-icon" + (a.key === "factory" ? " btn-icon-danger" : "")} disabled={a.disabled} title={a.title} onClick={onClick}>
      <Icon name={a.icon} /><span>{a.label}</span>
    </button>
  );
}

/** A button and the menu under it: the row's three dots, or the head's
 * *Actions ▾* when `trigger` is given. The menu closes when an act is chosen and
 * gives its trigger the focus back, so a keyboard user lands where they left;
 * Escape and a click outside close it the way every `Popover` does. Factory
 * reset, wherever it is in the list, is last and below a divider (#1801). */
function ActMenu({ label, acts, trigger, anchorRef, onCdsRecord }: {
  label: string; acts: Act[]; trigger?: ReactNode;
  anchorRef?: RefObject<HTMLButtonElement | null>; onCdsRecord?(): void;
}) {
  const own = useRef<HTMLButtonElement>(null);
  const anchor = anchorRef ?? own;
  const [open, setOpen] = useState(false);
  const choose = (act: () => void) => { setOpen(false); anchor.current?.focus(); act(); };
  return (
    <>
      {trigger
        ? (
          <button ref={anchor} type="button" className="btn btn-quiet btn-icon" aria-haspopup="true" aria-expanded={open}
            onClick={() => setOpen((o) => !o)}>
            {trigger}
          </button>
        )
        : (
          <button ref={anchor} type="button" className="btn btn-quiet btn-icon btn-more" aria-haspopup="true" aria-expanded={open}
            aria-label={label} title="Actions" onClick={() => setOpen((o) => !o)}>
            <Icon name="more" />
          </button>
        )}
      <Popover open={open} anchor={anchor} onClose={() => setOpen(false)} label={label}>
        <div className="act-menu">
          {acts.map((a, i) => (
            <Fragment key={a.key}>
              {a.key === "factory" && i > 0 && <hr className="act-divider" />}
              <ActButton act={a} onClick={() => choose(a.onClick)} />
            </Fragment>
          ))}
          {/* The record is no act, so it sits apart from them under a divider of its own, as a menu of
              one item: a real button the keyboard reaches, named by its words (#2118). */}
          {onCdsRecord && (
            <>
              {acts.length > 0 && <hr className="act-divider" />}
              <div role="menu" aria-label="CDS">
                <button type="button" role="menuitem" className="btn btn-quiet act-cds" onClick={() => choose(onCdsRecord)}>CDS record…</button>
              </div>
            </>
          )}
        </div>
      </Popover>
    </>
  );
}
